import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import Swal from "sweetalert2";
import {
  addAccommodationType,
  getAccommodationTypeById,
  updateAccommodationType,
} from "../../../services/AccommodationTypeService";
import { Button } from "react-bootstrap";
import NoImage from "../../../images/no-image.jpg";

const AddAccommodationType = () => {
  const [loading, setLoading] = useState(false);
  const [accommodationTypeName, setAccommodationTypeName] = useState("");
  const [accommodationTypeImage, setAccommodationTypeImage] = useState(null);
  const [existingAccommodationTypeImage, setExistingAccommodationTypeImage] =
    useState("");
  const hasFetched = useRef(false);
  const { id } = useParams();
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (id && !hasFetched.current) {
      hasFetched.current = true;
      const fetchAccommodationType = async () => {
        try {
          setLoading(true);
          const response = await getAccommodationTypeById(id);
          const accommodationType = response?.data;
          setAccommodationTypeName(accommodationType?.data?.title);
          setExistingAccommodationTypeImage(accommodationType?.data?.image || null);
        } catch (err) {
          console.error("Error fetching accommodation type data:", err);
        } finally {
          setLoading(false);
        }
      };
      fetchAccommodationType();
    }
  }, [id]);

  const validateForm = () => {
    const newErrors = {};

    if (!accommodationTypeName.trim()) {
      newErrors.accommodationTypeName = "Title is required";
    }

    setErrors(newErrors);

    // Return true if no errors
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    try {
      // --- VALIDATE FORMS ---
      const formValid = validateForm();
      if (!formValid) {
        return;
      }
      const fd = new FormData();
      console.log(accommodationTypeImage, "accommodationTypeImage");
      if (accommodationTypeImage) fd.append("image", accommodationTypeImage);
      const submitData = {
        title: accommodationTypeName,
      };

      Object.keys(submitData).forEach((key) => {
        fd.append(key, submitData[key]);
      });

      setLoading(true);

      if (id) {
        // --- UPDATE EXISTING COUNTRY ---
        await updateAccommodationType(id, fd);

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Accommodation Type details updated successfully",
        });
        navigate("/accommodation-type", {
          state: {
            page: location.state?.page || 1,
            searchTerm: location.state?.searchTerm || "",
          },
        });
      } else {
        // --- CREATE NEW COUNTRY ---
        const response = await addAccommodationType(fd);
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Accommodation Type added successfully",
          });
          navigate("/accommodation-type", {
            state: {
              page: location.state?.page || 1,
              searchTerm: location.state?.searchTerm || "",
            },
          });
        }
      }
    } catch (err) {
      console.error("Error in handleSave:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Operation failed",
      });
    } finally {
      // hide loader
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setAccommodationTypeImage(e.target.files[0]);
  };

  if (loading) {
    return (
      <div id="preloader">
        <div className="sk-three-bounce">
          <div className="sk-child sk-bounce1"></div>
          <div className="sk-child sk-bounce2"></div>
          <div className="sk-child sk-bounce3"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageTitle
        activeMenu={`${id ? "Edit Accommodation Type" : "Add Accommodation Type"
          }`}
        motherMenu="Accommodation Type"
        activeMenuLink={`${id ? `edit-accommodation-type/${id}` : "add-accommodation-type"
          }`}
        motherMenuLink={"accommodation-type"}
      />
      <div className="row">
        <div className="col-xl-12">
          <div className="row">
            <div className="col-xl-12 col-xxl-12">
              <div className="card h-auto">
                <div className="card-body">
                  <form>
                    <div
                      onClick={() => {
                        navigate("/accommodation-type", {
                          state: {
                            page: location.state?.page || 1,
                            searchTerm: location.state?.searchTerm || "",
                          },
                        });
                      }}
                      className="mb-4 d-flex align-items-center flex-wrap gap-2 cursor-pointer w-max"
                    >
                      <i className="fas fa-chevron-left"></i>
                      <div>Accommodation Type List</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Accommodation Type Image
                      </label>
                      <div>
                        <img
                          src={
                            accommodationTypeImage
                              ? URL.createObjectURL(accommodationTypeImage)
                              : existingAccommodationTypeImage || NoImage
                          }
                          alt="Accommodation Image"
                          width={100}
                          height={100}
                          className="rounded border mb-2"
                        />
                      </div>
                      <div className="change-btn d-flex align-items-center flex-wrap">
                        <input
                          type="file"
                          onChange={handleFileChange}
                          id="imageUpload"
                          className="d-none"
                        />
                        <label
                          htmlFor="imageUpload"
                          className="btn btn-primary ms-0"
                        >
                          Select Image
                        </label>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="title">
                        Title
                      </label>
                      <input
                        id="title"
                        type="text"
                        className="form-control w-full"
                        placeholder="Title"
                        value={accommodationTypeName} // bind value
                        onChange={(e) =>
                          setAccommodationTypeName(e.target.value)
                        } // update state
                      />
                      {errors?.accommodationTypeName && (
                        <div className="text-danger mb-3 mt-1" id="title-error">
                          {errors.accommodationTypeName}
                        </div>
                      )}
                    </div>
                  </form>
                </div>
              </div>
              <hr style={{ margin: "0px" }} className="mx-2" />
              <div className="card-footer border-0 text-end py-3 d-flex justify-content-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    navigate("/accommodation-type", {
                      state: {
                        page: location.state?.page || 1,
                        searchTerm: location.state?.searchTerm || "",
                      },
                    })
                  }}
                >
                  Back
                </Button>

                {/* Save / Update Button */}
                <Button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleSave}
                >
                  {id ? "Update" : "Save"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddAccommodationType;
